import React, { useState } from 'react';
import { TypewriterText } from '../ui/TypewriterText';
import { ShareButtons } from '../ui/ShareButtons';
import { SendHorizontal, RefreshCw, MessageSquare } from 'lucide-react';
import { FeedbackDialog } from '../feedback/FeedbackDialog';

interface ChatReportProps {
  report: string;
  shareId?: string | null;
  onClear?: () => void;
  deepAnalysis?: boolean;
}

export const ChatReport: React.FC<ChatReportProps> = ({ 
  report, 
  shareId, 
  onClear
}) => {
  const [showFeedback, setShowFeedback] = useState(false);

  const scrollToInput = () => {
    // Find the textarea element
    const textarea = document.querySelector('textarea');
    if (textarea) {
      textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      textarea.focus();
    }
  };
  
  return (
    <>
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 transition-colors">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Analysis Report</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFeedback(true)}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              title="Send feedback"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm">Feedback</span>
            </button>
            {onClear && (
              <button
                onClick={onClear}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="text-sm">Clear</span>
              </button>
            )}
          </div>
        </div>
        <div className="text-gray-700 dark:text-gray-200 break-words whitespace-pre-wrap text-sm leading-relaxed 
          [&_img]:inline [&_img]:h-4 [&_img]:w-4 [&_img]:align-middle [&_img]:mx-0.5
          [&_emoji]:inline [&_emoji]:align-middle [&_emoji]:leading-none [&_emoji]:text-base">
          <TypewriterText text={report} speed={30} />
        </div>

        {/* Continue Chat Link - Only shown when there's a report */}
        {report && (
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={scrollToInput}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm flex items-center gap-1.5 transition-colors"
            >
              <SendHorizontal className="w-4 h-4" />
              <span>Continue chat</span>
            </button>
          </div>
        )}

        {shareId && (
          <ShareButtons 
            title="Football Match Analysis" 
            text={`Check out this football match analysis: ${report.slice(0, 100)}...`}
            shareUrl={`${window.location.origin}/shared/${shareId}`}
          />
        )}
      </div>

      <FeedbackDialog 
        isOpen={showFeedback} 
        onClose={() => setShowFeedback(false)} 
      />
    </>
  );
};